<template>
  <div class="blog-page">
    <HeadNav selectedLanguage="en" page="blog" />

    <main>
      <section class="blog-hero" aria-labelledby="blog-title">
        <div class="blog-hero__inner">
          <p class="blog-kicker">SuperVPN Blog</p>
          <h1 id="blog-title">SuperVPN Guides and Troubleshooting</h1>
          <p class="blog-lead">
            Practical setup, connection, and troubleshooting articles for SuperVPN users.
          </p>
        </div>
      </section>

      <section class="blog-section" aria-label="SuperVPN articles">
        <div class="blog-grid">
          <router-link
            v-for="post in postList"
            :key="post.path"
            class="blog-card"
            :to="post.path"
          >
            <img :src="post.thumbnail" :alt="post.imageAlt" width="640" height="360" loading="lazy">
            <span class="blog-card__body">
              <time :datetime="post.date">{{ formatDate(post.date) }}</time>
              <span class="blog-card__title">{{ post.title }}</span>
              <span class="blog-card__desc">{{ post.description }}</span>
              <span class="blog-card__more">Read guide</span>
            </span>
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import HeadNav from '@/components/HeadNav.vue';
import { getBlogPostsSorted } from '@/data/posts.js';

export default {
  name: 'BlogsPage',
  components: {
    HeadNav,
  },
  computed: {
    postList() {
      return getBlogPostsSorted();
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@500;600;700&display=swap');

.blog-page {
  --page: #f3f7fb;
  --surface: #ffffff;
  --ink: #12263a;
  --body: #445568;
  --muted: #667789;
  --line: #d5e4ef;
  --brand: #1aade8;
  --brand-dark: #0d84b8;
  --shadow: 0 18px 40px rgba(18, 38, 58, 0.08);
  min-height: 100vh;
  background: linear-gradient(180deg, #f7fbfd 0%, var(--page) 46%, #eef3f8 100%);
  color: var(--body);
  font-family: 'Manrope', 'Segoe UI', sans-serif;
}

.blog-hero {
  border-bottom: 1px solid rgba(26, 173, 232, 0.1);
  background:
    linear-gradient(135deg, rgba(26, 173, 232, 0.12) 0%, rgba(255, 255, 255, 0.62) 54%, rgba(232, 169, 58, 0.08) 100%),
    linear-gradient(180deg, #eaf6fc 0%, #f7fbfd 100%);
}

.blog-hero__inner,
.blog-grid {
  width: min(1080px, calc(100% - 48px));
  margin: 0 auto;
}

.blog-hero__inner {
  padding: 58px 0 50px;
  text-align: center;
}

.blog-kicker {
  margin: 0 0 12px;
  color: var(--brand-dark);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  color: var(--ink);
  font-family: 'Sora', 'Manrope', sans-serif;
  font-size: clamp(30px, 4vw, 44px);
  line-height: 1.14;
  letter-spacing: 0;
}

.blog-lead {
  max-width: 680px;
  margin: 16px auto 0;
  font-size: 18px;
  line-height: 1.7;
}

.blog-section {
  padding: 48px 0 68px;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.blog-card {
  display: flex;
  flex-direction: column;
  padding: 18px;
  border: 1px solid rgba(26, 173, 232, 0.14);
  border-left: 4px solid var(--brand);
  border-radius: 8px;
  background: linear-gradient(180deg, var(--surface) 0%, #f8fbfe 100%);
  color: inherit;
  text-decoration: none !important;
  box-shadow: 0 10px 28px rgba(18, 38, 58, 0.04);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.blog-card:hover {
  transform: translateY(-3px);
  border-color: rgba(26, 173, 232, 0.35);
  box-shadow: var(--shadow);
}

.blog-card img {
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  display: block;
  object-fit: cover;
  border: 1px solid rgba(26, 173, 232, 0.12);
  border-radius: 8px;
  background: #ffffff;
}

.blog-card__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  padding-top: 16px;
}

time {
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;
}

.blog-card__title {
  margin-top: 8px;
  color: var(--ink);
  font-family: 'Sora', 'Manrope', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.32;
}

.blog-card__desc {
  margin-top: 8px;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.62;
}

.blog-card__more {
  margin-top: auto;
  padding-top: 16px;
  color: var(--brand-dark);
  font-size: 14px;
  font-weight: 800;
}

@media (max-width: 820px) {
  .blog-hero__inner,
  .blog-grid {
    width: min(100% - 28px, 1080px);
  }

  .blog-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .blog-hero__inner {
    padding: 42px 0 36px;
  }

  .blog-card {
    padding: 14px;
  }

  .blog-card__title {
    font-size: 17px;
  }

  .blog-card__desc {
    font-size: 14px;
    line-height: 1.5;
  }
}
</style>
